import React, { Fragment, useState, useEffect, useContext } from 'react';
import { ApiRequest, RegisteredInputsBuilder, ToastBuilder } from '../../../../../../../GlobalComponents/Builders';
import { buildSelectOptionsFromArray, parseJSONSafe } from '../../../../../../../GlobalComponents/Helpers';
import { Container, Row } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AccountContext } from '../../../../../../../GlobalComponents/Authentication/Accounts';
import { ContentLoader } from '../../../../../../../Layout/Loader';
import SectionCard from '../../../../../../../GlobalComponents/Common/Component/SectionCard';
import moment from 'moment';

import AssessmentHeader from './components/AssessmentHeader';
import LinkedJobs from './components/LinkedJobs';
import PhasesSection from './components/PhasesSection';
import OverviewSection from './components/OverviewSection';
import RecordCountsSection from './components/RecordCountsSection';
import SetupResourcesSection from './components/SetupResourcesSection';
import MigrationScopeSection from './components/MigrationScopeSection';

// Section definitions — drives the accordion layout
// Sections are conditionally shown based on job type (migration vs sync)
const SHARED_SECTIONS = [
    { key: 'overview', title: 'Project Overview', defaultOpen: true },
    { key: 'setup_resources', title: 'Setup Resources' },
    { key: 'notes', title: 'General Notes' },
];

const MIGRATION_SECTIONS = [
    { key: 'phases', title: 'Migration Phases' },
    { key: 'record_counts', title: 'Scoped Record Counts' },
    { key: 'data_quality', title: 'Data Quality & Cleaning' },
    { key: 'static_values', title: 'Static Values & Defaults' },
    { key: 'dependencies', title: 'Pre-Migration Dependencies' },
];

const SYNC_SECTIONS = [
    { key: 'sync_direction', title: 'Sync Direction & Scope' },
    { key: 'resource_mapping_notes', title: 'Resource Mapping & Context' },
    { key: 'sync_filters', title: 'Sync Filters & Conditions' },
    { key: 'required_fields', title: 'Required Fields' },
    { key: 'conflict_resolution', title: 'Conflict Resolution' },
    { key: 'scheduling', title: 'Scheduling & Triggers' },
    { key: 'error_handling', title: 'Error Handling Policies' },
];

// Build the full section list based on job type
const getSectionDefs = (jobType) => {
    const shared = [...SHARED_SECTIONS];
    if (jobType === 'migration') {
        // Insert migration_scope right after overview, then migration sections before notes
        shared.splice(1, 0, { key: 'migration_scope', title: 'Migration Scope' });
        const notesIdx = shared.findIndex(s => s.key === 'notes');
        shared.splice(notesIdx, 0, ...MIGRATION_SECTIONS);
    } else if (jobType === 'one-way-sync' || jobType === 'two-way-sync') {
        // Insert sync sections before notes
        const notesIdx = shared.findIndex(s => s.key === 'notes');
        shared.splice(notesIdx, 0, ...SYNC_SECTIONS);
    }
    return shared;
};

// Default empty structures per section
const SECTION_DEFAULTS = {
    overview: { source_system: '', destination_system: '', source_connection: '', destination_connection: '', scope_description: '', job_types: '' },
    phases: { rows: [{ phase: '1', data_import: '', status: '-', record_count: '', due_date: '', filters: '', custom_logic: '', input_required: '', notes: '' }] },
    setup_resources: {},
    record_counts: {},
    notes: { general: '' },

    // Migration-specific defaults
    migration_scope: { items: [] },
    data_quality: { cleaning_required: '', duplicate_handling: 'adopt', validation_rules: '', incomplete_records: '' },
    static_values: { hardcoded_or_derived: 'mixed', values: '' },
    dependencies: { csv_mapping_required: '', dynamic_imports: '', pre_migration_notes: '' },

    // Sync-specific defaults
    sync_direction: { direction: '', source_of_truth: '', initial_sync_strategy: '', scope_notes: '' },
    resource_mapping_notes: { source_resource_descriptions: '', destination_resource_descriptions: '', mapping_decisions: '' },
    sync_filters: { source_filters: '', destination_filters: '', notes: '' },
    required_fields: { source_required: '', destination_required: '', gap_analysis: '' },
    conflict_resolution: { strategy: '', field_level_rules: '', last_write_wins_exceptions: '' },
    scheduling: { trigger_type: '', frequency: '', batch_size: '', off_hours_only: '', notes: '' },
    error_handling: { retry_policy: '', max_retries: '3', dead_letter_action: '', notification_threshold: '', escalation_notes: '' },

    // ── v.0 Legacy Section Defaults (preserved for reference) ──
    // company_config: { company_types: '', site_naming: '', custom_fields_notes: '' },
    // contact_config: { validate_emails: '', validate_phones: '', missing_names_handling: '' },
    // custom_fields: { source_custom_fields: '', destination_mapping_notes: '' },
};

const DMTAssessment = () => {

    const { assessmentId } = useParams();
    const navigate = useNavigate();
    const { userSession, isAdmin } = useContext(AccountContext);

    const { register, handleSubmit, control, setValue, getValues, watch, formState: { errors } } = useForm();
    const { register: metaRegister, handleSubmit: metaHandleSubmit, control: metaControl, setValue: metaSetValue, formState: { errors: metaErrors } } = useForm();

    const [assessment, setAssessment] = useState(null);
    const [sections, setSections] = useState({});
    const [sectionMeta, setSectionMeta] = useState({});
    const [editingSections, setEditingSections] = useState({});
    const [linkedJobs, setLinkedJobs] = useState([]);
    const [availableJobs, setAvailableJobs] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);
    const [services, setServices] = useState([]);
    const [connections, setConnections] = useState([]);
    const [sourceResources, setSourceResources] = useState([]);
    const [dirtySections, setDirtySections] = useState({});
    const [savedSections, setSavedSections] = useState({});
    const [savedVersion, setSavedVersion] = useState(0);

    const statusOptions = buildSelectOptionsFromArray([
        { id: 'draft', value: 'Draft' },
        { id: 'in_progress', value: 'In Progress' },
        { id: 'awaiting_input', value: 'Awaiting Client Input' },
        { id: 'completed', value: 'Completed' },
        { id: 'archived', value: 'Archived' },
    ], { label: 'value', value: 'id' });

    const statusColors = { draft: 'secondary', in_progress: 'primary', awaiting_input: 'warning', completed: 'success', archived: 'dark' };

    // ── Data Loading ──

    const loadAssessment = () => {
        ApiRequest('GET', '/dmt/assessments/{assessmentId}', { pathParams: { assessmentId }, setIsProcessing }).then((res) => {
            if (!res.success || !res.data) {
                ToastBuilder('error', 'Assessment not found');
                return navigate(`${process.env.PUBLIC_URL}/data-migration/assessments`);
            }
            setAssessment(res.data);
            setLinkedJobs(res.data.jobs || []);
            metaSetValue('name', res.data.name);
            metaSetValue('description', res.data.description);
            metaSetValue('status', res.data.status);

            // Load sections separately
            ApiRequest('GET', '/dmt/assessments/{assessmentId}/sections', { pathParams: { assessmentId } }).then((sectionsRes) => {
                const sectionMap = {};
                const metaMap = {};
                const sectionRows = Array.isArray(sectionsRes.data) && sectionsRes.data.length > 0 ? sectionsRes.data : [];
                sectionRows.forEach((s) => {
                    sectionMap[s.section_key] = typeof s.section_data === 'string' ? parseJSONSafe(s.section_data) : s.section_data;
                    metaMap[s.section_key] = { updated_at: s.updated_at, updated_by: s.updated_by };
                });
                setSections(sectionMap);
                setSavedSections(JSON.parse(JSON.stringify(sectionMap)));
                setSectionMeta(metaMap);

                Object.keys(SECTION_DEFAULTS).forEach((key) => {
                    const data = sectionMap[key] || SECTION_DEFAULTS[key] || {};
                    Object.entries(data).forEach(([field, val]) => {
                        if (field !== 'rows') setValue(`${key}.${field}`, typeof val === 'object' ? JSON.stringify(val) : val);
                    });
                });
            });
        });
    };

    const loadAvailableJobs = () => {
        ApiRequest('GET', '/tools/{toolId}/jobs', { pathParams: { toolId: 'data-migration' } }).then((res) => {
            setAvailableJobs(Array.isArray(res.data) && res.data.length > 0 ? res.data : []);
        });
    };

    // ── Section Save ──

    const toggleEditing = (sectionKey) => {
        const currentlyEditing = editingSections[sectionKey];
        if (currentlyEditing) {
            // Cancelling — revert to last saved state
            const saved = savedSections[sectionKey] || SECTION_DEFAULTS[sectionKey] || {};
            // Revert sections state (for phases, record_counts, setup_resources, migration_scope)
            if (['phases', 'record_counts', 'setup_resources', 'migration_scope'].includes(sectionKey)) {
                setSections((prev) => ({ ...prev, [sectionKey]: JSON.parse(JSON.stringify(saved)) }));
            }
            // Revert react-hook-form values (for overview, etc.)
            Object.entries(saved).forEach(([field, val]) => {
                if (field !== 'rows') setValue(`${sectionKey}.${field}`, typeof val === 'object' ? JSON.stringify(val) : val);
            });
            setDirtySections((prev) => ({ ...prev, [sectionKey]: false }));
        }
        setEditingSections((prev) => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    const saveSection = (sectionKey) => {
        const allValues = getValues();
        let sectionData;

        if (sectionKey === 'phases' || sectionKey === 'record_counts' || sectionKey === 'setup_resources' || sectionKey === 'migration_scope') {
            // phases and record_counts are managed via setSections state, not react-hook-form
            sectionData = sections[sectionKey] || SECTION_DEFAULTS[sectionKey];
            // Sort phases by phase number before saving
            if (sectionKey === 'phases' && sectionData?.rows) {
                sectionData = { ...sectionData, rows: [...sectionData.rows].sort((a, b) => parseFloat(a.phase || 0) - parseFloat(b.phase || 0)) };
            }
        } else {
            // react-hook-form stores dotted names as nested objects: { overview: { source_system: '...' } }
            sectionData = allValues[sectionKey] || {};
        }

        ApiRequest('POST', '/dmt/assessments/{assessmentId}/sections', {
            pathParams: { assessmentId },
            body: { section_key: sectionKey, section_data: sectionData },
        }).then((res) => {
            if (res.success) {
                ToastBuilder('success', 'Section saved');
                setSections((prev) => ({ ...prev, [sectionKey]: sectionData }));
                setSavedSections((prev) => ({ ...prev, [sectionKey]: JSON.parse(JSON.stringify(sectionData)) }));
                setEditingSections((prev) => ({ ...prev, [sectionKey]: false }));
                setDirtySections((prev) => ({ ...prev, [sectionKey]: false }));
                setSavedVersion(v => v + 1);
            } else {
                ToastBuilder('error', res.error || 'Save failed');
            }
        });
    };

    const revertSection = (sectionKey) => {
        const saved = savedSections[sectionKey] || SECTION_DEFAULTS[sectionKey] || {};
        setSections((prev) => ({ ...prev, [sectionKey]: JSON.parse(JSON.stringify(saved)) }));
        setDirtySections((prev) => ({ ...prev, [sectionKey]: false }));
        ToastBuilder('success', 'Changes reverted');
    };

    // ── Assessment Meta Update ──

    const handleMetaUpdate = (formData) => {
        ApiRequest('PATCH', '/dmt/assessments/{assessmentId}', { pathParams: { assessmentId }, body: formData }).then((res) => {
            if (res.success) {
                ToastBuilder('success', 'Assessment updated');
                loadAssessment();
            }
        });
    };

    // ── Render Helper ──

    const renderField = (sectionKey, fieldName, label, type = 'text', options = {}) => {
        const isEditing = editingSections[sectionKey] && isEditable;
        const currentVal = getValues(`${sectionKey}.${fieldName}`) || sections[sectionKey]?.[fieldName] || '';

        if (!isEditing) {
            let displayVal = currentVal;
            if (type === 'reactselect' && currentVal && options.options) {
                const match = options.options.find(opt => opt.value === currentVal);
                if (match) displayVal = match.label;
            }
            const lblSize = options.viewLabelSize || 4;
            const valSize = options.viewInputSize || 8;
            return (
                <div className='row mb-2'>
                    <div className={`col-sm-${lblSize}`}><strong>{label}</strong></div>
                    <div className={`col-sm-${valSize}`}><span style={{ whiteSpace: 'pre-wrap' }}>{displayVal || <span className='text-muted'>—</span>}</span></div>
                </div>
            );
        }

        const inputConfig = {
            label,
            name: `${sectionKey}.${fieldName}`,
            type,
            labelSize: 2,
            inputSize: 7,
            offset: 1,
            size: 12,
            ...options,
        };

        // Remove helpText from inputConfig — it's rendered separately below
        delete inputConfig.helpText;

        if (type === 'textarea') {
            inputConfig.settings = { handleTabs: true, resize: true, ...(options.settings || {}) };
            inputConfig.style = { height: 100, ...(options.style || {}) };
        }

        return (
            <Fragment>
                <RegisteredInputsBuilder
                    registrator={register}
                    registratorErrors={errors}
                    control={control}
                    inputs={[inputConfig]}
                />
                {options.helpText && (
                    <small className='text-muted d-block mb-2' style={{ fontSize: 11, whiteSpace: 'pre-wrap', marginTop: -8, paddingLeft: `${(((options.offset || 1) + (options.labelSize || 2)) / 12 * 100).toFixed(1)}%` }}>{options.helpText}</small>
                )}
            </Fragment>
        );
    };

    // ── Init ──

    useEffect(() => {
        document.title = 'Discovery Assessment';
        loadAssessment();
        loadAvailableJobs();
        ApiRequest('GET', '/services').then((res) => {
            setServices(Array.isArray(res.data) && res.data.length > 0 ? res.data : []);
        });
        ApiRequest('GET', '/connections').then((res) => {
            setConnections(Array.isArray(res.data) && res.data.length > 0 ? res.data : []);
        });
    }, []);

    // Re-fetch source resources when source_system changes (e.g. after saving overview)
    const currentSourceSystem = sections.overview?.source_system || '';
    useEffect(() => {
        if (!currentSourceSystem) {
            setSourceResources([]);
            return;
        }
        const sourceService = services.find(s => s.name === currentSourceSystem);
        if (sourceService) {
            ApiRequest('GET', '/services/{serviceId}/resources', { pathParams: { serviceId: sourceService.service_id } }).then((res) => {
                if (Array.isArray(res.data) && res.data[0]?.resources) {
                    const allResources = res.data[0].resources;
                    const setupResourceIds = new Set(allResources.filter(r => parseInt(r.is_setup_resource) === 1).map(r => r.resource_id));
                    setSourceResources(allResources.filter(r => r.resource && (!r.parent_resource_id || !setupResourceIds.has(r.parent_resource_id))));
                } else {
                    setSourceResources([]);
                }
            });
        }
    }, [currentSourceSystem, services]);

    if (isProcessing || !assessment) return <ContentLoader />;

    const isEditable = assessment.status !== 'archived';

    // Sections that are always interactive (no edit mode needed)
    const alwaysInteractiveSections = ['setup_resources', 'record_counts'];

    // Derive section list based on job type
    const jobType = sections.overview?.job_types || watch('overview.job_types') || '';
    const SECTION_DEFS = getSectionDefs(jobType);

    // ── Render ──

    return (
        <Fragment>
            <AssessmentHeader
                assessment={assessment}
                statusColors={statusColors}
                statusOptions={statusOptions}
                metaRegister={metaRegister}
                metaErrors={metaErrors}
                metaControl={metaControl}
                metaSetValue={metaSetValue}
                metaHandleSubmit={metaHandleSubmit}
                handleMetaUpdate={handleMetaUpdate}
                navigate={navigate}
                sectionDefs={SECTION_DEFS}
                sections={sections}
                assessmentId={assessmentId}
                onSectionDeleted={(key) => {
                    setSections(prev => { const next = { ...prev }; delete next[key]; return next; });
                    setSavedSections(prev => { const next = { ...prev }; delete next[key]; return next; });
                    setDirtySections(prev => { const next = { ...prev }; delete next[key]; return next; });
                }}
            />

            <LinkedJobs
                assessmentId={assessmentId}
                linkedJobs={linkedJobs}
                availableJobs={availableJobs}
                register={register}
                errors={errors}
                control={control}
                handleSubmit={handleSubmit}
                onRefresh={loadAssessment}
            />

            {/* Dynamic Sections */}
            <Row>
                <Container>
                    {SECTION_DEFS.map(({ key, title, defaultOpen }) => {
                        const isAlwaysInteractive = alwaysInteractiveSections.includes(key);
                        return (
                        <SectionCard
                            key={key}
                            title={title}
                            defaultOpen={defaultOpen}
                            editable={isAlwaysInteractive ? false : isEditable}
                            editing={isAlwaysInteractive ? false : editingSections[key]}
                            onEditToggle={isAlwaysInteractive ? undefined : () => toggleEditing(key)}
                            onSave={isAlwaysInteractive || key === 'migration_scope' ? undefined : () => saveSection(key)}
                            headerRight={
                                isAlwaysInteractive && isEditable && dirtySections[key] ? (
                                    <div className='d-flex gap-2'>
                                        <button className='btn btn-sm btn-outline-secondary' onClick={() => revertSection(key)}>
                                            Undo
                                        </button>
                                        <button className='btn btn-sm btn-danger' onClick={() => saveSection(key)}>
                                            ● Save Changes
                                        </button>
                                    </div>
                                ) : undefined
                            }
                            actions={
                                key === 'migration_scope' && editingSections[key] && dirtySections[key] ? (
                                    <button className='btn btn-sm btn-danger' onClick={() => saveSection(key)}>
                                        ● Save Changes
                                    </button>
                                ) : undefined
                            }
                            subtitle={sectionMeta[key]?.updated_at ? 'Updated ' + moment.utc(sectionMeta[key].updated_at * 1000).fromNow() : sections[key] ? 'Saved' : 'Not yet filled'}
                        >
                            {key === 'overview' && (
                                <OverviewSection
                                    renderField={renderField}
                                    setValue={setValue}
                                    watch={watch}
                                    sections={sections}
                                    assessment={assessment}
                                    services={services}
                                    connections={connections}
                                    isEditing={editingSections['overview'] && isEditable}
                                    isAdmin={isAdmin}
                                />
                            )}

                            {key === 'phases' && (
                                <PhasesSection
                                    sections={sections}
                                    setSections={setSections}
                                    isEditing={editingSections['phases'] && isEditable}
                                    sourceResources={sourceResources}
                                    sourceConnectionId={sections.overview?.source_connection || null}
                                    assessmentId={assessmentId}
                                />
                            )}

                            {key === 'setup_resources' && (
                                <SetupResourcesSection
                                    sections={sections}
                                    setSections={setSections}
                                    sourceResources={sourceResources}
                                    sourceConnectionId={sections.overview?.source_connection || null}
                                    assessmentId={assessmentId}
                                    onDirtyChange={(dirty) => setDirtySections(prev => ({ ...prev, setup_resources: dirty }))}
                                />
                            )}

                            {key === 'record_counts' && (
                                <RecordCountsSection
                                    sections={sections}
                                    setSections={setSections}
                                    sourceResources={sourceResources}
                                    sourceConnectionId={sections.overview?.source_connection || null}
                                    assessmentId={assessmentId}
                                    onDirtyChange={(dirty) => setDirtySections(prev => ({ ...prev, record_counts: dirty }))}
                                />
                            )}

                            {/* ═══════════════════════════════════════════════════════════
                                MIGRATION-SPECIFIC SECTIONS
                               ═══════════════════════════════════════════════════════════ */}

                            {key === 'migration_scope' && (
                                <MigrationScopeSection
                                    sections={sections}
                                    setSections={setSections}
                                    services={services}
                                    savedVersion={savedVersion}
                                    isEditing={editingSections['migration_scope'] && isEditable}
                                    onDirtyChange={(dirty) => setDirtySections(prev => ({ ...prev, migration_scope: dirty }))}
                                />
                            )}

                            {key === 'data_quality' && (
                                <Fragment>
                                    {renderField(key, 'cleaning_required', 'Is data cleaning required?', 'textarea', { style: { height: 80 }, placeholder: 'Describe any data cleaning needed before migration...', helpText: 'e.g., "Source has 500 duplicate companies that need merging before migration. Contact emails have inconsistent formatting (some missing @domain)."' })}
                                    {renderField(key, 'duplicate_handling', 'Duplicate Handling Strategy', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'adopt', value: 'Adopt existing record and link' },
                                            { id: 'create_new', value: 'Create new and change unique identifiers' },
                                            { id: 'skip', value: 'Skip from migration' },
                                            { id: 'merge', value: 'Merge into existing record' },
                                        ], { label: 'value', value: 'id' }),
                                        helpText: 'What happens when a record already exists in the destination? Adopt = link to existing, Create New = make a copy, Skip = ignore it.',
                                    })}
                                    {renderField(key, 'validation_rules', 'Validation Rules', 'textarea', { placeholder: 'Define validation rules to apply during migration...', style: { height: 100 }, helpText: 'e.g., "Validate email formats, reject records with no company name, phone numbers must be 10+ digits."' })}
                                    {renderField(key, 'incomplete_records', 'How to handle incomplete records?', 'textarea', { placeholder: 'What to do with records missing required fields...', helpText: 'e.g., "Skip contacts without a name. Companies without a type default to \'Customer\'. Flag tickets without a board for manual review."' })}
                                </Fragment>
                            )}

                            {key === 'static_values' && (
                                <Fragment>
                                    <p className='mb-3 text-muted' style={{ fontSize: 13 }}>
                                        Static values are hardcoded defaults applied to migrated records by resource — values that must be set to a fixed value.
                                        <br />
                                        <strong>Examples:</strong> Default territory, tax code, company type, billing terms, SLA, time zone, work role.
                                    </p>
                                    {renderField(key, 'hardcoded_or_derived', 'Value Strategy', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'hardcoded', value: 'Hardcoded — same value for all records' },
                                            { id: 'derived', value: 'Derived — computed from source data' },
                                            { id: 'mixed', value: 'Mixed — some hardcoded, some derived' },
                                        ], { label: 'value', value: 'id' }),
                                        helpText: 'Hardcoded = every record gets the same value. Derived = value is computed from source data (e.g., territory based on state).',
                                    })}
                                    {renderField(key, 'values', 'Static Values', 'textarea', {
                                        style: { height: 200, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Add static values as needed...',
                                        helpText: 'Describe the hardcoded fields and whether values are truly static or dynamic based on another field of that resource.\nFormat: Field = Value (one per line). Text or ID values.\n\ne.g.,\nDefault Site Name = Main Site\nTerritory = East Coast (static)\nTax Code = Standard (static)\nCompany Type = derived from source "classification" field\nBilling Terms = Net 30 (static)\nTime Zone = America/New_York (static)\nWork Role = derived from source "role" field',
                                    })}
                                </Fragment>
                            )}

                            {key === 'dependencies' && (
                                <Fragment>
                                    {renderField(key, 'csv_mapping_required', 'CSV mapping file required?', 'reactselect', {
                                        options: buildSelectOptionsFromArray([{ id: 'yes', value: 'Yes' }, { id: 'no', value: 'No' }, { id: 'tbd', value: 'TBD' }], { label: 'value', value: 'id' }),
                                        helpText: 'Does the client need to provide a CSV/spreadsheet with custom mappings (e.g., old board → new board)?',
                                    })}
                                    {renderField(key, 'dynamic_imports', 'Dynamic values to import before migration?', 'textarea', { placeholder: 'What reference data must exist in destination first?', style: { height: 100 }, helpText: 'e.g., "Board statuses, work types, and company types must be created in CWM before tickets can be migrated."' })}
                                    {renderField(key, 'pre_migration_notes', 'Pre-Migration Dependencies', 'textarea', { style: { height: 150 }, placeholder: 'What must be completed before migration can begin?', helpText: '\u2022 Setup resources imported and value-mapped?\n\u2022 Custom fields created in destination?\n\u2022 Client sign-off on scope and mapping?\n\u2022 Test migration completed and reviewed?' })}
                                </Fragment>
                            )}

                            {/* ═══════════════════════════════════════════════════════════
                                SYNC-SPECIFIC SECTIONS
                               ═══════════════════════════════════════════════════════════ */}

                            {key === 'sync_direction' && (
                                <Fragment>
                                    {renderField(key, 'direction', 'Sync Direction', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'source_to_dest', value: 'Source \u2192 Destination (one-way)' },
                                            { id: 'dest_to_source', value: 'Destination \u2192 Source (one-way)' },
                                            { id: 'bidirectional', value: 'Bidirectional (two-way)' },
                                            { id: 'conditional', value: 'Conditional \u2014 direction varies by resource' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'source_of_truth', 'Source of Truth', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'source', value: 'Source system is authoritative' },
                                            { id: 'destination', value: 'Destination system is authoritative' },
                                            { id: 'newest', value: 'Most recently updated record wins' },
                                            { id: 'per_field', value: 'Varies per field (see conflict resolution)' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'initial_sync_strategy', 'Initial Sync Strategy', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'full_sync', value: 'Full sync \u2014 process all existing records' },
                                            { id: 'incremental', value: 'Incremental \u2014 only new/modified since cutover date' },
                                            { id: 'manual_seed', value: 'Manual seed \u2014 pre-populate then sync ongoing' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'scope_notes', 'Scope & Direction Notes', 'textarea', { style: { height: 120 }, placeholder: 'Describe any nuances about sync direction...', helpText: 'e.g., "Tickets sync bidirectionally but time entries only flow from source to destination. New companies created in either system should sync to the other."' })}
                                </Fragment>
                            )}

                            {key === 'resource_mapping_notes' && (
                                <Fragment>
                                    <p className='text-muted mb-3' style={{ fontSize: 13 }}>
                                        Describe how resources in the source system relate to resources in the destination.
                                        This helps developers understand the business context when configuring resource mappings.
                                        <br /><br />
                                        <strong>For each resource:</strong> What is it? How is it used? What does it contain?
                                        What is the equivalent in the other system, and why?
                                    </p>
                                    {renderField(key, 'source_resource_descriptions', 'Source System Resources', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 200, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Describe each resource: what is it, how is it used, what does it contain?',
                                        helpText: '\u2022 Boards \u2014 Used to scope tickets by team/department. Board owners = team leads.\n  - Board Types, Subtypes, Items \u2014 Levels of ticket categorization\n\u2022 Priorities \u2014 4 levels: Critical, High, Normal, Low\n\u2022 Statuses \u2014 Per-board. Typical flow: New \u2192 In Progress \u2192 Completed \u2192 Closed',
                                    })}
                                    {renderField(key, 'destination_resource_descriptions', 'Destination System Resources', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 200, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Describe each resource in the destination system...',
                                        helpText: '\u2022 Categories \u2014 3-level hierarchy (Category \u2192 Sub-category \u2192 Item Category)\n\u2022 Queues \u2014 Similar to boards but less granular. Used for routing, not categorization.\n\u2022 Priorities \u2014 4 levels: Urgent, High, Medium, Low\n\u2022 Statuses \u2014 Global across all queues. Flow: Open \u2192 Pending \u2192 Resolved \u2192 Closed',
                                    })}
                                    {renderField(key, 'mapping_decisions', 'Mapping Decisions & Rationale', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 180, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Document which source resources map to which destination resources and why...',
                                        helpText: '\u2022 CWM Board Items \u2192 FS Categories (NOT board types/subtypes)\n  Rationale: Board items are the most granular and map 1:1 to FS categories.\n\u2022 CWM Board \u2192 FS Queue (routing only, not categorization)\n\u2022 CWM Priority "Critical" \u2192 FS Priority "Urgent" (name differs, same meaning)',
                                    })}
                                </Fragment>
                            )}

                            {key === 'sync_filters' && (
                                <Fragment>
                                    <p className='text-muted mb-3' style={{ fontSize: 13 }}>
                                        Define the filters that scope which records are included in the sync.
                                        These are the actual conditions applied at runtime.
                                    </p>
                                    {renderField(key, 'source_filters', 'Source System Filters', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 120, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'e.g.,\nTickets: board/id in (1, 5, 12) AND status/name != "Closed"\nCompanies: type/name = "Customer" AND status = "Active"\nContacts: company/id in (synced company IDs)',
                                    })}
                                    {renderField(key, 'destination_filters', 'Destination System Filters', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 120, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'e.g.,\nTickets: queue_id in (3, 7) AND status != 5\nContacts: client_id in (synced client IDs)',
                                    })}
                                    {renderField(key, 'notes', 'Filter Notes', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 80 },
                                        placeholder: 'Any notes about filter logic, edge cases, or records that should be explicitly excluded',
                                    })}
                                </Fragment>
                            )}

                            {key === 'required_fields' && (
                                <Fragment>
                                    <p className='text-muted mb-3' style={{ fontSize: 13 }}>
                                        Document fields that are required for record creation/update in each system.
                                        This helps identify gaps where source data may not satisfy destination requirements.
                                    </p>
                                    {renderField(key, 'source_required', 'Source System Required Fields', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 120, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Resource: Required Fields\n\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\nTickets: summary, board/id, status/id, company/id\nCompanies: name, type/id\nContacts: firstName OR lastName, company/id',
                                    })}
                                    {renderField(key, 'destination_required', 'Destination System Required Fields', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 120, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Resource: Required Fields\n\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\nTickets: subject, requester_id, status, priority\nClients: name\nContacts: name, client_id',
                                    })}
                                    {renderField(key, 'gap_analysis', 'Field Gap Analysis', 'textarea', {
                                        labelSize: 3, inputSize: 9,
                                        style: { height: 100 },
                                        placeholder: 'Identify fields required in destination that may not exist in source...',
                                        helpText: 'e.g., "Destination requires requester_id on tickets but source has no equivalent \u2014 will default to a system contact."',
                                    })}
                                </Fragment>
                            )}

                            {key === 'conflict_resolution' && (
                                <Fragment>
                                    {renderField(key, 'strategy', 'Default Conflict Strategy', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'source_wins', value: 'Source always wins' },
                                            { id: 'destination_wins', value: 'Destination always wins' },
                                            { id: 'last_write_wins', value: 'Last write wins (most recent update)' },
                                            { id: 'manual_review', value: 'Flag for manual review' },
                                            { id: 'field_level', value: 'Field-level rules (see below)' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'field_level_rules', 'Field-Level Conflict Rules', 'textarea', {
                                        style: { height: 150, fontFamily: 'monospace', fontSize: 12 },
                                        placeholder: 'Define per-field override rules when the default strategy doesn\'t apply...',
                                        helpText: 'status: destination wins (techs update in real-time)\npriority: source wins (client controls priority)\nnotes: append (never overwrite, merge both)\ncustom_fields: last write wins',
                                    })}
                                    {renderField(key, 'last_write_wins_exceptions', 'Exceptions & Edge Cases', 'textarea', {
                                        style: { height: 80 },
                                        placeholder: 'Any scenarios where the default strategy should NOT apply.\ne.g., "If a ticket is closed in destination, never reopen from source sync."',
                                    })}
                                </Fragment>
                            )}

                            {key === 'scheduling' && (
                                <Fragment>
                                    {renderField(key, 'trigger_type', 'Sync Trigger', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'scheduled', value: 'Scheduled (cron/interval)' },
                                            { id: 'webhook', value: 'Webhook/event-driven' },
                                            { id: 'hybrid', value: 'Hybrid \u2014 webhook + scheduled catchup' },
                                            { id: 'manual', value: 'Manual trigger only' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'frequency', 'Sync Frequency', 'text', { placeholder: 'e.g., Every 5 minutes, Every hour, Real-time via webhook' })}
                                    {renderField(key, 'batch_size', 'Batch Size', 'text', { placeholder: 'e.g., 100 records per batch, or "all" for full sync each run' })}
                                    {renderField(key, 'off_hours_only', 'Off-Hours Restriction', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'no', value: 'No \u2014 sync runs anytime' },
                                            { id: 'yes', value: 'Yes \u2014 sync only during off-hours' },
                                            { id: 'heavy_only', value: 'Heavy operations off-hours only, light sync anytime' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'notes', 'Scheduling Notes', 'textarea', { style: { height: 80 }, placeholder: 'Any timing constraints, blackout windows, or dependencies on other processes' })}
                                </Fragment>
                            )}

                            {key === 'error_handling' && (
                                <Fragment>
                                    {renderField(key, 'retry_policy', 'Retry Policy', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'immediate', value: 'Immediate retry' },
                                            { id: 'exponential_backoff', value: 'Exponential backoff' },
                                            { id: 'next_cycle', value: 'Retry on next sync cycle' },
                                            { id: 'manual', value: 'No auto-retry \u2014 manual intervention' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'max_retries', 'Max Retries Before Flagging', 'text', { placeholder: 'e.g., 3, 5, 10' })}
                                    {renderField(key, 'dead_letter_action', 'After Max Retries (Dead Letter)', 'reactselect', {
                                        options: buildSelectOptionsFromArray([
                                            { id: 'skip', value: 'Skip record and continue' },
                                            { id: 'pause_resource', value: 'Pause sync for that resource' },
                                            { id: 'pause_all', value: 'Pause entire sync job' },
                                            { id: 'notify_only', value: 'Notify team but continue syncing' },
                                        ], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'notification_threshold', 'Error Notification Threshold', 'text', { placeholder: 'e.g., 3 consecutive failures, 10% error rate' })}
                                    {renderField(key, 'escalation_notes', 'Escalation & Recovery Notes', 'textarea', {
                                        style: { height: 100 },
                                        placeholder: 'Who gets notified? What\'s the recovery process?',
                                        helpText: 'e.g., "After 5 failures, email dev team. After 20, pause job and notify client."',
                                    })}
                                </Fragment>
                            )}

                            {/* ═══════════════════════════════════════════════════════════
                                SHARED SECTIONS
                               ═══════════════════════════════════════════════════════════ */}

                            {key === 'notes' && (
                                <Fragment>
                                    {renderField(key, 'general', 'General Notes', 'textarea', { style: { height: 200 }, placeholder: 'Any additional notes, assumptions, open questions, or client communications...' })}
                                </Fragment>
                            )}

                            {/* ═══════════════════════════════════════════════════════════
                                v.0 LEGACY SECTIONS (preserved — do not remove)
                               ═══════════════════════════════════════════════════════════

                            {key === 'company_config' && (
                                <Fragment>
                                    {renderField(key, 'company_types', 'Company Types to Migrate', 'textarea', { placeholder: 'Customer, Vendor, Prospect' })}
                                    {renderField(key, 'site_naming', 'Default Site Naming', 'text', { placeholder: 'Headquarters, Main, Default Site' })}
                                    {renderField(key, 'custom_fields_notes', 'Custom Fields Notes', 'textarea')}
                                </Fragment>
                            )}

                            {key === 'contact_config' && (
                                <Fragment>
                                    {renderField(key, 'validate_emails', 'Validate email formats?', 'reactselect', {
                                        options: buildSelectOptionsFromArray([{ id: 'yes', value: 'Yes' }, { id: 'no', value: 'No' }, { id: 'tbd', value: 'TBD' }], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'validate_phones', 'Validate phone numbers?', 'reactselect', {
                                        options: buildSelectOptionsFromArray([{ id: 'yes', value: 'Yes' }, { id: 'no', value: 'No' }, { id: 'tbd', value: 'TBD' }], { label: 'value', value: 'id' }),
                                    })}
                                    {renderField(key, 'missing_names_handling', 'How to handle missing names?', 'textarea')}
                                </Fragment>
                            )}

                            {key === 'custom_fields' && (
                                <Fragment>
                                    {renderField(key, 'source_custom_fields', 'Source Custom Fields', 'textarea', { placeholder: 'List custom fields from source system that need migration', style: { height: 120 } })}
                                    {renderField(key, 'destination_mapping_notes', 'Destination Mapping Notes', 'textarea', { placeholder: 'How should source custom fields map to destination?', style: { height: 120 } })}
                                </Fragment>
                            )}

                            ═══════════════════════════════════════════════════════════ */}
                        </SectionCard>
                        );
                    })}
                </Container>
            </Row>
        </Fragment>
    );
};

export default DMTAssessment;
